import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { COPY } from '../copy';
import { formatCount } from '../format/format';
import { toSearch, withParams } from './catalogParams';
import type { useCategories } from './useCategories';

const selectedSx = {
  '&.Mui-selected .MuiListItemText-primary': { fontWeight: 600 },
};

interface CategoryRailProps {
  categories: ReturnType<typeof useCategories>;
  selectedId: number | null;
  onNavigate?: () => void;
}

export function CategoryRail({ categories, selectedId, onNavigate }: CategoryRailProps) {
  const [sp] = useSearchParams();
  const { status, items, reload } = categories;

  return (
    <>
      <Typography variant="h2" component="h2" id="categories-heading" sx={{ px: 2, py: 1.5 }}>
        {COPY.categoriesHeading}
      </Typography>
      {status === 'loading' && <LinearProgress aria-label={COPY.loadingCategories} />}
      {status === 'error' && (
        <Alert
          severity="error"
          action={
            <Button onClick={reload} color="inherit">
              {COPY.tryAgain}
            </Button>
          }
        >
          {COPY.categoriesErrorTitle}
        </Alert>
      )}
      <List aria-labelledby="categories-heading" disablePadding>
        <ListItem disablePadding>
          <ListItemButton
            component={RouterLink}
            to={{ search: toSearch(withParams(sp, { category: null })) }}
            selected={selectedId === null}
            aria-current={selectedId === null ? 'true' : undefined}
            onClick={onNavigate}
            nativeButton={false}
            sx={selectedSx}
          >
            <ListItemText primary={COPY.allForms} slotProps={{ primary: { variant: 'body2' } }} />
          </ListItemButton>
        </ListItem>
        {items.map((category) => (
          <ListItem key={category.id} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={{ search: toSearch(withParams(sp, { category: category.id })) }}
              selected={selectedId === category.id}
              aria-current={selectedId === category.id ? 'true' : undefined}
              onClick={onNavigate}
              nativeButton={false}
              sx={selectedSx}
            >
              <ListItemText primary={category.name} slotProps={{ primary: { variant: 'body2' } }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontVariantNumeric: 'tabular-nums', ml: 2 }}
              >
                {formatCount(category.formCount)}
              </Typography>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );
}
